/**
 * EquipmentType — frontend mirror of the NestJS EquipmentType model
 * (Phase 3a).
 *
 * EquipmentType is a *catalog* row: it describes a kind of equipment
 * (e.g. "Wärtsilä 8L46F main engine") that can be installed on
 * multiple vessels. Each Equipment row references one EquipmentType.
 *
 * Catalog scope:
 *   - ownerCompanyId === null  → global ISI catalog, visible to all
 *                                companies and editable only by ISI
 *                                admins.
 *   - ownerCompanyId === '...' → custom type owned by that company,
 *                                visible/editable only within it.
 */

export const EQUIPMENT_TYPE_CATEGORIES = [
  'propulsion',
  'power',
  'auxiliary',
  'hvac',
  'safety',
  'navigation',
  'communication',
  'deck',
  'hull',
  'accommodation',
  'electrical',
  'hydraulic',
  'instrumentation',
  'other',
] as const;
export type EquipmentTypeCategory = (typeof EQUIPMENT_TYPE_CATEGORIES)[number];

/**
 * L4.5 — declarative configuration of monotonic counters expected
 * on equipment of this type. Each entry drives the validation
 * pipeline (`monotonic`, `maxRatePerDay`) and the "due" widget
 * (`frequency`).
 */
export interface CounterFieldConfig {
  field: string;
  label: string;
  unit: string;
  frequency: 'weekly' | 'monthly' | 'quarterly';
  maxRatePerDay: number;
  monotonic: boolean;
}

export interface EquipmentType {
  id: string;
  name: string;
  code: string;
  category: EquipmentTypeCategory;
  description: string | null;
  manufacturer: string | null;
  modelRef: string | null;
  ownerCompanyId: string | null;
  metadata: Record<string, unknown> | null;
  /** L4.5 — null when this type doesn't expose any counter. */
  counterFields: CounterFieldConfig[] | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** True when this type belongs to the global ISI catalog. */
export function isGlobalType(t: EquipmentType): boolean {
  return t.ownerCompanyId === null;
}
